import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container, Table, Button, Badge, Modal, Form, Tabs, Tab,
  Spinner, Alert, Row, Col, Card
} from "react-bootstrap";
import { useAuth } from "../context/AuthContext.js";
import { useLanguage } from "../context/LanguageContext.js";
import { t } from "../utils/translations.js";
import { callXeroFunction } from "../firebase.js";
import LoadingSpinner from "../components/common/LoadingSpinner.js";
import { AlertMessage } from "../components/common/AlertMessage.js";
import EmptyState from "../components/common/EmptyState.js";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db as firestoreDb } from "../firebase.js";

// Helper: parse "HH:MM" to decimal hours
function timeToDecimal(timeStr) {
  if (!timeStr) return 0;
  const parts = timeStr.split(":");
  const h = parseInt(parts[0]) || 0;
  const m = parseInt(parts[1]) || 0;
  return h + m / 60;
}

// Helper: format decimal hours to "HH:MM"
function decimalToTime(decimal) {
  if (!decimal) return "00:00";
  const h = Math.floor(decimal);
  const m = Math.round((decimal - h) * 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// Helper: get Monday of a week from a date
function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

// Helper: format date to YYYY-MM-DD (local timezone, matching ColaboradorPage's date-fns format)
function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Helper: format date display DD/MM
function formatDateDisplay(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}`;
}

// Status badge component
function StatusBadge({ status, language }) {
  const config = {
    pending: { bg: "secondary", label: t(language, "payrollPending") },
    approved: { bg: "primary", label: t(language, "payrollApproved") },
    sent_to_xero: { bg: "warning", label: t(language, "payrollSentToXero") },
    paid: { bg: "success", label: t(language, "payrollPaid") },
  };
  const c = config[status] || config.pending;
  return <Badge bg={c.bg}>{c.label}</Badge>;
}

export default function PayrollPage() {
  const { user } = useAuth();
  const { language } = useLanguage();

  // Week navigation
  const [currentMonday, setCurrentMonday] = useState(() => getMonday(new Date()));

  // Data states
  const [users, setUsers] = useState([]);
  const [weeklyReports, setWeeklyReports] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [xeroEmployees, setXeroEmployees] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);

  // UI states
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [alert, setAlert] = useState({ message: "", variant: "success" });
  const [activeTab, setActiveTab] = useState("payroll");

  // Manual hours override: { [uid]: "HH:MM" }
  const [manualHours, setManualHours] = useState({});

  // Modal states
  const [showPayslip, setShowPayslip] = useState(false);
  const [payslipData, setPayslipData] = useState(null);
  const [showFechamento, setShowFechamento] = useState(false);
  const [fechamentoData, setFechamentoData] = useState(null);

  const weekStart = formatDate(currentMonday);
  const weekEnd = useMemo(() => {
    const end = new Date(currentMonday);
    end.setDate(end.getDate() + 6);
    return formatDate(end);
  }, [currentMonday]);

  // Navigate weeks
  const prevWeek = useCallback(() => {
    setCurrentMonday(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 7);
      return d;
    });
  }, []);

  const nextWeek = useCallback(() => {
    setCurrentMonday(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 7);
      return d;
    });
  }, []);

  // Load all data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch users, mappings, and payroll records in parallel
      const [usersRes, mappingsRes, recordsRes] = await Promise.all([
        callXeroFunction("getUsers", {}, { method: "GET" }),
        callXeroFunction("getPayrollMappings", {}, { method: "GET" }),
        callXeroFunction("getPayrollRecords", {}, { method: "GET", queryParams: { weekStart } }),
      ]);

      setUsers(usersRes?.users || []);
      setMappings(mappingsRes?.mappings || []);
      setPayrollRecords(recordsRes?.records || []);

      // Fetch weekly reports from Firestore
      const reportsSnap = await getDocs(
        query(collection(firestoreDb, "weeklyReports"), where("weekStart", "==", weekStart))
      );
      const reports = reportsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setWeeklyReports(reports);
    } catch (error) {
      console.error("Error loading payroll data:", error);
      setAlert({ message: error.message, variant: "danger" });
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  // Load Xero employees (only on Setup tab)
  const loadXeroEmployees = useCallback(async () => {
    try {
      const res = await callXeroFunction("getPayrollEmployees", {}, { method: "GET" });
      setXeroEmployees(res?.employees || []);
    } catch (error) {
      console.error("Error loading Xero employees:", error);
      setAlert({ message: `Xero employees: ${error.message}`, variant: "danger" });
    }
  }, []);

  // Reset manual hours when week changes
  useEffect(() => { setManualHours({}); }, [weekStart]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (activeTab === "setup") loadXeroEmployees();
  }, [activeTab, loadXeroEmployees]);

  // Index data for O(1) lookups
  const mappingsByUid = useMemo(() => {
    const map = {};
    mappings.forEach(m => { map[m.firebase_uid] = m; });
    return map;
  }, [mappings]);

  const reportsByUid = useMemo(() => {
    const map = {};
    weeklyReports.forEach(r => { map[r.uid] = r; });
    return map;
  }, [weeklyReports]);

  const recordsByUid = useMemo(() => {
    const map = {};
    payrollRecords.forEach(r => { map[r.firebase_uid] = r; });
    return map;
  }, [payrollRecords]);

  // Only show employees mapped to Xero (linked via Setup tab)
  const staffUsers = useMemo(() => {
    return users.filter(u => mappingsByUid[u.uid]);
  }, [users, mappingsByUid]);

  // Unmapped users
  const unmappedUsers = useMemo(() => {
    return users.filter(u => !mappingsByUid[u.uid]);
  }, [users, mappingsByUid]);

  // Table data: combine reports + records + mappings + manual overrides
  const tableData = useMemo(() => {
    return staffUsers.map(u => {
      const report = reportsByUid[u.uid];
      const record = recordsByUid[u.uid];
      const mapping = mappingsByUid[u.uid];
      const reportHours = report ? timeToDecimal(report.weekTotalHours || report.weekWorkHours || "0:0") : 0;
      const manual = manualHours[u.uid];
      const hours = manual !== undefined ? timeToDecimal(manual) : reportHours;
      const rate = parseFloat(mapping?.hourly_rate || record?.hourly_rate || 0);
      const gross = record?.gross_amount ? parseFloat(record.gross_amount) : hours * rate;
      const status = record?.status || "pending";
      const reportDisplay = report ? (report.weekTotalHours || report.weekWorkHours || "00:00") : "00:00";

      // Parse payslip data if available
      let payslip = null;
      if (record?.payslip_data) {
        payslip = typeof record.payslip_data === "string"
          ? (() => { try { return JSON.parse(record.payslip_data); } catch { return null; } })()
          : record.payslip_data;
      }

      return {
        uid: u.uid,
        displayName: u.displayName,
        report,
        record,
        mapping,
        payslip,
        hours,
        reportHours,
        hoursDisplay: manual !== undefined ? manual : reportDisplay,
        isManual: manual !== undefined,
        rate,
        gross: record?.gross_amount ? parseFloat(record.gross_amount) : Math.round(hours * rate * 100) / 100,
        net: record?.net_amount ? parseFloat(record.net_amount) : null,
        status,
      };
    });
  }, [staffUsers, reportsByUid, recordsByUid, mappingsByUid, manualHours]);

  // Totals
  const totals = useMemo(() => {
    return tableData.reduce((acc, row) => ({
      hours: acc.hours + row.hours,
      gross: acc.gross + row.gross,
      net: acc.net + (row.net || 0),
    }), { hours: 0, gross: 0, net: 0 });
  }, [tableData]);

  // === Actions ===

  // Approve + auto-calculate estimate from Xero employee data
  const handleApprove = useCallback(async (row) => {
    setActionLoading(row.uid);
    try {
      await callXeroFunction("approvePayroll", {
        firebaseUid: row.uid,
        displayName: row.displayName,
        weekStart,
        fechamentoHours: row.hours,
        hourlyRate: row.rate,
      });
      // Auto-calculate estimate (fetches PAYE, KiwiSaver, ACC from Xero)
      try {
        await callXeroFunction("calculatePayroll", {
          firebaseUid: row.uid,
          weekStart,
          fechamentoHours: row.hours,
        });
      } catch (e) {
        console.log("Estimate calc failed:", e.message);
      }
      setAlert({ message: t(language, "payrollApprovedSuccess"), variant: "success" });
      await loadData();
    } catch (error) {
      setAlert({ message: error.message, variant: "danger" });
    } finally {
      setActionLoading(null);
    }
  }, [weekStart, language, loadData]);

  const handleUnapprove = useCallback(async (row) => {
    setActionLoading(row.uid);
    try {
      await callXeroFunction("unapprovePayroll", {
        firebaseUid: row.uid,
        weekStart,
      });
      setAlert({ message: t(language, "payrollUnapprovedSuccess"), variant: "info" });
      await loadData();
    } catch (error) {
      setAlert({ message: error.message, variant: "danger" });
    } finally {
      setActionLoading(null);
    }
  }, [weekStart, language, loadData]);

  // Send ALL approved employees to Xero at once
  const handleSendAllToXero = useCallback(async () => {
    const approved = tableData.filter(r => r.status === "approved");
    if (approved.length === 0) return;
    setActionLoading("sendAll");
    const results = [];
    const allWarnings = [];

    for (const row of approved) {
      try {
        const hoursPerDay = {};
        if (row.report?.totalHoursPerDay) {
          Object.entries(row.report.totalHoursPerDay).forEach(([day, timeStr]) => {
            hoursPerDay[day] = timeToDecimal(timeStr);
          });
        }
        const result = await callXeroFunction("createPayrun", {
          firebaseUid: row.uid,
          weekStart,
          hoursPerDay,
        });
        results.push({ name: row.displayName, ok: true, ...result });
        if (result?.warnings?.length) allWarnings.push(`${row.displayName}: ${result.warnings.join("; ")}`);
      } catch (e) {
        results.push({ name: row.displayName, ok: false, error: e.message });
        allWarnings.push(`${row.displayName}: ${e.message}`);
      }
    }

    const successCount = results.filter(r => r.ok).length;
    if (allWarnings.length > 0) {
      setAlert({ message: `${successCount}/${approved.length} ${t(language, "payrollSentSuccess")}. ${allWarnings.join(" | ")}`, variant: "warning" });
    } else {
      setAlert({ message: `${successCount}/${approved.length} ${t(language, "payrollSentSuccess")}`, variant: "success" });
    }
    await loadData();
    setActionLoading(null);
  }, [tableData, weekStart, language, loadData]);

  const handleApprovePayRun = useCallback(async (row) => {
    if (!row.record?.xero_payrun_id) {
      setAlert({ message: "No pay run ID found", variant: "danger" });
      return;
    }
    setActionLoading(row.uid);
    try {
      await callXeroFunction("approvePayrun", {
        firebaseUid: row.uid,
        weekStart,
        payRunId: row.record.xero_payrun_id,
      });
      setAlert({ message: t(language, "payrollPayrunApproved"), variant: "success" });
      await loadData();
    } catch (error) {
      setAlert({ message: error.message, variant: "danger" });
    } finally {
      setActionLoading(null);
    }
  }, [weekStart, language, loadData]);

  const handleViewPayslip = useCallback((row) => {
    if (row.record?.payslip_data) {
      let data = row.record.payslip_data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch (e) { /* ignore */ }
      }
      setPayslipData({ ...data, displayName: row.displayName, weekStart });
      setShowPayslip(true);
    }
  }, [weekStart]);

  const handleViewFechamento = useCallback((row) => {
    if (row.report) {
      setFechamentoData({ ...row.report, displayName: row.displayName, numWorkers: row.report.numWorkers || {} });
      setShowFechamento(true);
    }
  }, []);

  // Approve all pending + auto-calculate estimates
  const handleApproveAll = useCallback(async () => {
    const pending = tableData.filter(r => r.status === "pending" && r.hours > 0 && r.rate > 0);
    if (pending.length === 0) return;
    setActionLoading("all");
    // Approve all
    for (const row of pending) {
      try {
        await callXeroFunction("approvePayroll", {
          firebaseUid: row.uid,
          displayName: row.displayName,
          weekStart,
          fechamentoHours: row.hours,
          hourlyRate: row.rate,
        });
      } catch (e) {
        console.error(`Error approving ${row.displayName}:`, e);
      }
    }
    // Calculate estimates for all (fetches PAYE, KiwiSaver, ACC from Xero)
    for (const row of pending) {
      try {
        await callXeroFunction("calculatePayroll", {
          firebaseUid: row.uid,
          weekStart,
          fechamentoHours: row.hours,
        });
      } catch (e) {
        console.error(`Error estimating ${row.displayName}:`, e);
      }
    }
    setAlert({ message: t(language, "payrollAllApproved"), variant: "success" });
    await loadData();
    setActionLoading(null);
  }, [tableData, weekStart, language, loadData]);

  // === Mapping handlers ===
  const [mappingForm, setMappingForm] = useState({});

  const handleSaveMapping = useCallback(async (firebaseUid, displayName) => {
    const xeroId = mappingForm[firebaseUid]?.xeroEmployeeId;
    if (!xeroId) return;

    const xeroEmp = xeroEmployees.find(e => e.employeeID === xeroId);
    setActionLoading(firebaseUid);
    try {
      await callXeroFunction("createPayrollMapping", {
        firebaseUid,
        displayName,
        xeroEmployeeId: xeroId,
        xeroEmployeeName: xeroEmp?.fullName || "",
        hourlyRate: mappingForm[firebaseUid]?.hourlyRate || 0,
      });
      setAlert({ message: t(language, "payrollMappingSaved"), variant: "success" });
      await loadData();
    } catch (error) {
      setAlert({ message: error.message, variant: "danger" });
    } finally {
      setActionLoading(null);
    }
  }, [mappingForm, xeroEmployees, language, loadData]);

  const handleDeleteMapping = useCallback(async (uid) => {
    setActionLoading(uid);
    try {
      await callXeroFunction("deletePayrollMapping", {}, { method: "DELETE", id: uid });
      setAlert({ message: t(language, "payrollMappingDeleted"), variant: "info" });
      await loadData();
    } catch (error) {
      setAlert({ message: error.message, variant: "danger" });
    } finally {
      setActionLoading(null);
    }
  }, [language, loadData]);

  // === History tab ===
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await callXeroFunction("getPayrollHistory", {}, { method: "GET" });
      setHistory(res?.records || []);
    } catch (error) {
      console.error("Error loading history:", error);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "history") loadHistory();
  }, [activeTab, loadHistory]);

  // === Render ===

  if (loading) return <LoadingSpinner fullPage />;

  const renderActions = (row) => {
    const isLoading = actionLoading === row.uid;
    const btnSize = "sm";

    const viewBtn = row.report ? (
      <Button size={btnSize} variant="outline-info" onClick={() => handleViewFechamento(row)}>
        {t(language, "payrollViewReport")}
      </Button>
    ) : null;

    switch (row.status) {
      case "pending":
        return (
          <div className="d-flex gap-1 flex-wrap">
            {viewBtn}
            <Button
              size={btnSize}
              variant="primary"
              disabled={isLoading || row.hours === 0}
              onClick={() => handleApprove(row)}
            >
              {isLoading ? <Spinner size="sm" /> : t(language, "payrollApproveBtn")}
            </Button>
          </div>
        );
      case "approved":
        return (
          <div className="d-flex gap-1 flex-wrap">
            {viewBtn}
            <Badge bg="primary" className="d-flex align-items-center">{t(language, "payrollApproved")}</Badge>
            <Button
              size={btnSize}
              variant="outline-secondary"
              disabled={isLoading}
              onClick={() => handleUnapprove(row)}
            >
              {t(language, "payrollUnapprove")}
            </Button>
          </div>
        );
      case "sent_to_xero":
        return (
          <div className="d-flex gap-1 flex-wrap">
            {viewBtn}
            {row.record?.xero_payrun_id && (
              <Button
                size={btnSize}
                variant="success"
                disabled={isLoading}
                onClick={() => handleApprovePayRun(row)}
              >
                {isLoading ? <Spinner size="sm" /> : t(language, "payrollApprovePayrun")}
              </Button>
            )}
            {row.record?.payslip_data && (
              <Button size={btnSize} variant="outline-info" onClick={() => handleViewPayslip(row)}>
                {t(language, "payrollViewPayslip")}
              </Button>
            )}
            <Button
              size={btnSize}
              variant="outline-danger"
              disabled={isLoading}
              onClick={() => handleUnapprove(row)}
            >
              {isLoading ? <Spinner size="sm" /> : t(language, "payrollReset")}
            </Button>
          </div>
        );
      case "paid":
        return (
          <div className="d-flex gap-1">
            {viewBtn}
            {row.record?.payslip_data && (
              <Button size={btnSize} variant="outline-info" onClick={() => handleViewPayslip(row)}>
                {t(language, "payrollViewPayslip")}
              </Button>
            )}
            <Badge bg="success" className="d-flex align-items-center">{t(language, "payrollPaid")}</Badge>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Container fluid className="py-3" style={{ maxWidth: 1200 }}>
      {/* Header with week navigation */}
      <div className="d-flex justify-content-between flex-wrap gap-2 align-items-center mb-4">
        <h4 className="mb-0">{t(language, "payrollTitle")}</h4>
        <div className="d-flex align-items-center gap-2">
          <Button variant="outline-secondary" size="sm" onClick={prevWeek}>&larr;</Button>
          <span className="fw-bold">
            {formatDateDisplay(weekStart)} - {formatDateDisplay(weekEnd)}
          </span>
          <Button variant="outline-secondary" size="sm" onClick={nextWeek}>&rarr;</Button>
        </div>
      </div>

      <AlertMessage
        message={alert.message}
        variant={alert.variant}
        onClose={() => setAlert({ message: "", variant: "success" })}
      />

      {unmappedUsers.length > 0 && activeTab !== "setup" && (
        <Alert variant="warning" className="mb-3">
          {t(language, "payrollUnmappedWarning").replace("{count}", unmappedUsers.length)}
          {" "}
          <Alert.Link onClick={() => setActiveTab("setup")}>
            {t(language, "payrollGoToSetup")}
          </Alert.Link>
        </Alert>
      )}

      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
        {/* TAB: Setup */}
        <Tab eventKey="setup" title={t(language, "payrollSetup")}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">{t(language, "payrollMappingTitle")}</h5>
              <p className="text-muted small mb-3">{t(language, "payrollMappingDesc")}</p>

              <Table responsive hover size="sm">
                <thead>
                  <tr>
                    <th>{t(language, "payrollAppUser")}</th>
                    <th>{t(language, "payrollXeroEmployee")}</th>
                    <th>{t(language, "payrollHourlyRate")}</th>
                    <th style={{ width: 150 }}>{t(language, "payrollActions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => {
                    const existing = mappingsByUid[u.uid];
                    return (
                      <tr key={u.uid}>
                        <td className="align-middle">
                          <strong>{u.displayName}</strong>
                          <br /><small className="text-muted">{u.email}</small>
                        </td>
                        <td className="align-middle">
                          {existing ? (
                            <span>{existing.xero_employee_name} <Badge bg="success" className="ms-1">Linked</Badge></span>
                          ) : (
                            <Form.Select
                              size="sm"
                              value={mappingForm[u.uid]?.xeroEmployeeId || ""}
                              onChange={e => setMappingForm(prev => ({
                                ...prev,
                                [u.uid]: { ...prev[u.uid], xeroEmployeeId: e.target.value }
                              }))}
                            >
                              <option value="">{t(language, "payrollSelectEmployee")}</option>
                              {xeroEmployees.map(emp => (
                                <option key={emp.employeeID} value={emp.employeeID}>
                                  {emp.fullName}
                                </option>
                              ))}
                            </Form.Select>
                          )}
                        </td>
                        <td className="align-middle" style={{ width: 120 }}>
                          {existing ? (
                            <span>${parseFloat(existing.hourly_rate || 0).toFixed(2)}</span>
                          ) : (
                            <Form.Control
                              type="number"
                              size="sm"
                              step="0.5"
                              placeholder="$/hr"
                              value={mappingForm[u.uid]?.hourlyRate || ""}
                              onChange={e => setMappingForm(prev => ({
                                ...prev,
                                [u.uid]: { ...prev[u.uid], hourlyRate: e.target.value }
                              }))}
                            />
                          )}
                        </td>
                        <td className="align-middle">
                          {existing ? (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              disabled={actionLoading === u.uid}
                              onClick={() => handleDeleteMapping(u.uid)}
                            >
                              {actionLoading === u.uid ? <Spinner size="sm" /> : t(language, "delete")}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="primary"
                              disabled={!mappingForm[u.uid]?.xeroEmployeeId || actionLoading === u.uid}
                              onClick={() => handleSaveMapping(u.uid, u.displayName)}
                            >
                              {actionLoading === u.uid ? <Spinner size="sm" /> : t(language, "save")}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {xeroEmployees.length === 0 && (
                <EmptyState
                  message={t(language, "payrollNoXeroEmployees")}
                  icon="👥"
                  variant="warning"
                />
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* TAB: Payroll Semanal */}
        <Tab eventKey="payroll" title={t(language, "payrollWeekly")}>
          {tableData.length === 0 ? (
            <EmptyState
              message={t(language, "payrollNoData")}
              icon="📋"
            />
          ) : (
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>{t(language, "payrollEmployee")}</th>
                      <th className="text-center">{t(language, "payrollHours")}</th>
                      <th className="text-center d-none d-md-table-cell">{t(language, "payrollRate")}</th>
                      <th className="text-center d-none d-md-table-cell">{t(language, "payrollGross")}</th>
                      <th className="text-center d-none d-md-table-cell">{t(language, "payrollDeductions")}</th>
                      <th className="text-center d-none d-md-table-cell">{t(language, "payrollNet")}</th>
                      <th className="text-center">{t(language, "status")}</th>
                      <th>{t(language, "payrollActions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map(row => (
                      <tr key={row.uid}>
                        <td className="align-middle">
                          <strong>{row.displayName}</strong>
                          {!row.mapping && (
                            <Badge bg="danger" className="ms-1" style={{ fontSize: "0.6rem" }}>
                              {t(language, "payrollNotMapped")}
                            </Badge>
                          )}
                        </td>
                        <td className="text-center align-middle">
                          {row.status === "pending" ? (
                            <Form.Control
                              type="text"
                              size="sm"
                              className="text-center"
                              style={{ width: 80, display: "inline-block" }}
                              placeholder="HH:MM"
                              value={manualHours[row.uid] !== undefined ? manualHours[row.uid] : (row.report ? (row.report.weekTotalHours || row.report.weekWorkHours || "") : "")}
                              onChange={e => setManualHours(prev => ({ ...prev, [row.uid]: e.target.value }))}
                            />
                          ) : (
                            row.hoursDisplay
                          )}
                          {row.isManual && row.reportHours > 0 && (
                            <div className="small text-muted" style={{ fontSize: "0.7rem" }}>
                              {t(language, "payrollOriginal")}: {decimalToTime(row.reportHours)}
                            </div>
                          )}
                        </td>
                        <td className="text-center align-middle d-none d-md-table-cell">
                          {row.rate > 0 ? `$${row.rate.toFixed(2)}` : "-"}
                        </td>
                        <td className="text-center align-middle d-none d-md-table-cell">
                          {row.gross > 0 ? `$${row.gross.toFixed(2)}` : "-"}
                        </td>
                        <td className="text-center align-middle d-none d-md-table-cell">
                          {row.payslip ? (
                            <div className="small text-start" style={{ fontSize: "0.75rem", lineHeight: 1.4 }}>
                              {row.payslip.isEstimate ? (
                                <>
                                  <div>PAYE ({row.payslip.taxCode || "M"}): <span className="text-danger">-${parseFloat(row.payslip.tax || 0).toFixed(2)}</span></div>
                                  <div>ACC 1.6%: <span className="text-danger">-${parseFloat(row.payslip.accLevy || 0).toFixed(2)}</span></div>
                                  {row.payslip.hasKiwiSaver && (
                                    <>
                                      <div>KiwiSaver {row.payslip.kiwiSaverPct}%: <span className="text-danger">-${parseFloat(row.payslip.kiwiSaverEmployee || 0).toFixed(2)}</span></div>
                                      <div>KiwiSaver Emp 3%: <span className="text-success">+${parseFloat(row.payslip.kiwiSaverEmployer || 0).toFixed(2)}</span></div>
                                    </>
                                  )}
                                  {parseFloat(row.payslip.studentLoan || 0) > 0 && (
                                    <div>Student Loan: <span className="text-danger">-${parseFloat(row.payslip.studentLoan).toFixed(2)}</span></div>
                                  )}
                                  <div className="text-muted fst-italic mt-1" style={{ fontSize: "0.65rem" }}>({t(language, "payrollEstimate")})</div>
                                </>
                              ) : (
                                <>
                                  {parseFloat(row.payslip.tax || 0) > 0 && (
                                    <div>PAYE: <span className="text-danger">-${parseFloat(row.payslip.tax).toFixed(2)}</span></div>
                                  )}
                                  {row.payslip.deductionLines?.map((d, i) => (
                                    <div key={i}>{d.deductionName || d.DeductionName || "Ded"}: <span className="text-danger">-${parseFloat(d.amount || d.Amount || 0).toFixed(2)}</span></div>
                                  ))}
                                  {row.payslip.superLines?.map((s, i) => (
                                    <div key={i}>
                                      {s.superannuationLineName || s.SuperannuationLineName || "KiwiSaver"}:
                                      <span className={s.calculationType === "EmployerContribution" || s.CalculationType === "EmployerContribution" ? "text-success" : "text-danger"}>
                                        {s.calculationType === "EmployerContribution" || s.CalculationType === "EmployerContribution" ? " +" : " -"}
                                        ${parseFloat(s.amount || s.Amount || 0).toFixed(2)}
                                      </span>
                                    </div>
                                  ))}
                                </>
                              )}
                            </div>
                          ) : "-"}
                        </td>
                        <td className="text-center align-middle d-none d-md-table-cell">
                          {row.net ? (
                            <strong className="text-success">${row.net.toFixed(2)}</strong>
                          ) : "-"}
                        </td>
                        <td className="text-center align-middle">
                          <StatusBadge status={row.status} language={language} />
                        </td>
                        <td className="align-middle">{renderActions(row)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-light fw-bold">
                    <tr>
                      <td>{t(language, "payrollTotal")}</td>
                      <td className="text-center">{decimalToTime(totals.hours)}</td>
                      <td className="d-none d-md-table-cell"></td>
                      <td className="text-center d-none d-md-table-cell">${totals.gross.toFixed(2)}</td>
                      <td className="d-none d-md-table-cell"></td>
                      <td className="text-center d-none d-md-table-cell">
                        {totals.net > 0 ? <span className="text-success">${totals.net.toFixed(2)}</span> : "-"}
                      </td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tfoot>
                </Table>
              </Card.Body>
              <Card.Footer className="d-flex gap-2 justify-content-end flex-wrap">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={actionLoading === "all" || tableData.every(r => r.status !== "pending")}
                  onClick={handleApproveAll}
                >
                  {actionLoading === "all" ? <Spinner size="sm" className="me-1" /> : null}
                  {t(language, "payrollApproveAll")}
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  disabled={actionLoading === "sendAll" || tableData.every(r => r.status !== "approved")}
                  onClick={handleSendAllToXero}
                >
                  {actionLoading === "sendAll" ? <Spinner size="sm" className="me-1" /> : null}
                  {t(language, "payrollSendAllToXero")}
                </Button>
              </Card.Footer>
            </Card>
          )}
        </Tab>

        {/* TAB: History */}
        <Tab eventKey="history" title={t(language, "payrollHistory")}>
          {historyLoading ? (
            <LoadingSpinner />
          ) : history.length === 0 ? (
            <EmptyState
              message={t(language, "payrollNoHistory")}
              icon="📊"
            />
          ) : (
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0" size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>{t(language, "payrollWeek")}</th>
                      <th>{t(language, "payrollEmployee")}</th>
                      <th className="text-center">{t(language, "payrollHours")}</th>
                      <th className="text-center">{t(language, "payrollGross")}</th>
                      <th className="text-center">{t(language, "payrollNet")}</th>
                      <th className="text-center">{t(language, "status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(rec => (
                      <tr key={rec.id}>
                        <td>{formatDateDisplay(rec.week_start)}</td>
                        <td>{rec.display_name || rec.xero_employee_name}</td>
                        <td className="text-center">
                          {rec.fechamento_hours ? decimalToTime(parseFloat(rec.fechamento_hours)) : "-"}
                        </td>
                        <td className="text-center">
                          {rec.gross_amount ? `$${parseFloat(rec.gross_amount).toFixed(2)}` : "-"}
                        </td>
                        <td className="text-center">
                          {rec.net_amount ? `$${parseFloat(rec.net_amount).toFixed(2)}` : "-"}
                        </td>
                        <td className="text-center">
                          <StatusBadge status={rec.status} language={language} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </Tab>
      </Tabs>

      {/* Modal: Fechamento Semanal */}
      <Modal show={showFechamento} onHide={() => setShowFechamento(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {t(language, "payrollFechamentoTitle")} - {fechamentoData?.displayName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {fechamentoData && (() => {
            const dayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
            const dayLabels = language === "en"
              ? { Monday: "Monday", Tuesday: "Tuesday", Wednesday: "Wednesday", Thursday: "Thursday", Friday: "Friday", Saturday: "Saturday", Sunday: "Sunday" }
              : { Monday: "Segunda", Tuesday: "Terça", Wednesday: "Quarta", Thursday: "Quinta", Friday: "Sexta", Saturday: "Sábado", Sunday: "Domingo" };
            const days = fechamentoData.days || {};
            const totalHours = fechamentoData.totalHoursPerDay || {};
            const lunchMin = fechamentoData.lunchMinDay || {};
            const travelMin = fechamentoData.travelMinDay || {};
            const workers = fechamentoData.numWorkers || {};

            return (
              <div>
                {dayNames.map(day => {
                  const entries = days[day];
                  if (!entries || entries.length === 0) return null;
                  const nw = parseInt(workers[day]) || 1;

                  return (
                    <Card key={day} className="mb-2 border">
                      <Card.Header className="py-1 px-2 bg-light d-flex justify-content-between align-items-center">
                        <strong>{dayLabels[day]}</strong>
                        <span className="small text-muted">
                          {nw > 1 ? `${nw} ${t(language, "payrollWorkers")}` : ""}
                        </span>
                      </Card.Header>
                      <Card.Body className="py-1 px-2">
                        <Table borderless size="sm" className="mb-0">
                          <tbody>
                            {entries.map((e, i) => {
                              const hrs = e.editedHours || (e.__manual ? e.originalHours : null);
                              const displayHrs = hrs || (() => {
                                if (!e.originalHours) return "00:00";
                                if (nw <= 1) return e.originalHours;
                                const parts = e.originalHours.split(":");
                                const totalMin = (parseInt(parts[0]) || 0) * 60 + (parseInt(parts[1]) || 0);
                                const perPerson = Math.round(totalMin / nw);
                                return `${String(Math.floor(perPerson / 60)).padStart(2, "0")}:${String(perPerson % 60).padStart(2, "0")}`;
                              })();
                              const edited = !!e.editedHours;

                              return (
                                <tr key={i}>
                                  <td className="py-0">
                                    <span style={{ color: edited ? "#d32f2f" : "inherit", fontWeight: edited ? 600 : "normal" }}>
                                      {e.client}
                                    </span>
                                    {e.__address && (
                                      <small className="text-muted ms-1">({e.__address})</small>
                                    )}
                                    {e.justification && (
                                      <small className="text-warning ms-1" title={e.justification}>*</small>
                                    )}
                                  </td>
                                  <td className="py-0 text-end text-nowrap" style={{ width: 60 }}>
                                    {displayHrs}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                        <div className="d-flex justify-content-between small border-top pt-1 mt-1">
                          <span>
                            <strong>{t(language, "payrollWorkHours")}:</strong> {totalHours[day] || "00:00"}
                          </span>
                          {(parseInt(lunchMin[day]) > 0 || parseInt(travelMin[day]) > 0) && (
                            <span className="text-muted">
                              {parseInt(lunchMin[day]) > 0 && `Break: ${lunchMin[day]}min`}
                              {parseInt(lunchMin[day]) > 0 && parseInt(travelMin[day]) > 0 && " | "}
                              {parseInt(travelMin[day]) > 0 && `${t(language, "payrollTravel")}: ${travelMin[day]}min`}
                            </span>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  );
                })}

                <hr />
                <Row className="fw-bold">
                  <Col xs={6}>{t(language, "payrollWorkHoursTotal")}:</Col>
                  <Col className="text-end">{fechamentoData.weekWorkHours || "00:00"}</Col>
                </Row>
                <Row>
                  <Col xs={6}>{t(language, "payrollFechamentoTotal")}:</Col>
                  <Col className="text-end fw-bold text-primary">{fechamentoData.weekTotalHours || "00:00"}</Col>
                </Row>
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div>
            {fechamentoData && (() => {
              const row = tableData.find(r => r.uid === fechamentoData.uid);
              if (row && row.status === "pending" && row.hours > 0) {
                return (
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={actionLoading === row.uid}
                    onClick={() => { handleApprove(row); setShowFechamento(false); }}
                  >
                    {actionLoading === row.uid ? <Spinner size="sm" className="me-1" /> : null}
                    {t(language, "payrollApproveBtn")}
                  </Button>
                );
              }
              return null;
            })()}
          </div>
          <Button variant="secondary" onClick={() => setShowFechamento(false)}>
            {t(language, "close")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal: Payslip */}
      <Modal show={showPayslip} onHide={() => setShowPayslip(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t(language, "payrollPayslipTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {payslipData && (
            <div>
              <h5>{payslipData.displayName}</h5>
              <p className="text-muted">{t(language, "payrollWeek")}: {formatDateDisplay(payslipData.weekStart)}</p>

              {payslipData.earningsLines?.length > 0 && (
                <>
                  <h6 className="mt-3">{t(language, "payrollEarnings")}</h6>
                  <Table size="sm" bordered>
                    <tbody>
                      {payslipData.earningsLines.map((line, i) => (
                        <tr key={i}>
                          <td>{line.EarningsLineName || line.Description || "Earnings"}</td>
                          <td className="text-end">${parseFloat(line.Amount || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              <Row className="mb-2">
                <Col xs={6}><strong>{t(language, "payrollGross")}:</strong></Col>
                <Col className="text-end"><strong>${parseFloat(payslipData.grossPay || 0).toFixed(2)}</strong></Col>
              </Row>

              {payslipData.deductionLines?.length > 0 && (
                <>
                  <h6 className="mt-3">{t(language, "payrollDeductions")}</h6>
                  <Table size="sm" bordered>
                    <tbody>
                      {payslipData.deductionLines.map((line, i) => (
                        <tr key={i}>
                          <td>{line.DeductionName || line.Description || "Deduction"}</td>
                          <td className="text-end text-danger">-${parseFloat(line.Amount || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              {parseFloat(payslipData.tax || 0) > 0 && (
                <Row className="mb-2">
                  <Col xs={6}>PAYE:</Col>
                  <Col className="text-end text-danger">-${parseFloat(payslipData.tax).toFixed(2)}</Col>
                </Row>
              )}

              {payslipData.superLines?.length > 0 && (
                <>
                  <h6 className="mt-3">KiwiSaver</h6>
                  <Table size="sm" bordered>
                    <tbody>
                      {payslipData.superLines.map((line, i) => (
                        <tr key={i}>
                          <td>{line.SuperannuationLineName || "KiwiSaver"}</td>
                          <td className="text-end">
                            {line.CalculationType === "EmployerContribution" ? "+" : "-"}
                            ${parseFloat(line.Amount || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              <hr />
              <Row>
                <Col xs={6}><h5>{t(language, "payrollNetPay")}:</h5></Col>
                <Col className="text-end"><h5 className="text-success">${parseFloat(payslipData.netPay || 0).toFixed(2)}</h5></Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPayslip(false)}>
            {t(language, "close")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
